import {
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { getThemingData } from "lib/eventThemingApi";
import { ThemingData } from "types/Theming";
import LoadingScreen from "components/LoadingScreen";
import useEventRouting from "hooks/useEventRouting";
import { EventThemingHead } from "./AppThemingHead";
import FEATURE_FLAGS from "./featureFlags/flags";
import { useFeatureFlag } from "./featureFlags";

export interface AppThemingContextType {
  theming: ThemingData | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

const AppThemingContext = createContext<AppThemingContextType | undefined>(
  undefined,
);

export const AppThemingProvider = ({ children }: { children: ReactNode }) => {
  const { eventId } = useEventRouting();
  // If feature is disabled or no eventId, scripts and styles are "loaded" (not needed)
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const isEventThemingEnabled = useFeatureFlag(FEATURE_FLAGS.eventTheming);

  const {
    data: theming = null,
    isLoading,
    isError,
    error,
  } = useQuery<ThemingData, Error>({
    queryKey: ["eventTheming", eventId, isEventThemingEnabled],
    queryFn: async () => {
      return await getThemingData(isEventThemingEnabled ? eventId : undefined);
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    cacheTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Reset scriptsLoaded when theming changes
  useEffect(() => {
    setScriptsLoaded(false);
  }, [theming]);

  const contextValue: AppThemingContextType = {
    theming,
    isLoading,
    isError,
    error,
  };

  return (
    <AppThemingContext.Provider value={contextValue}>
      {theming && (
        <EventThemingHead
          theming={theming}
          onResourcesLoaded={() => setScriptsLoaded(true)}
        />
      )}
      {isLoading || !scriptsLoaded || !theming ? <LoadingScreen /> : children}
    </AppThemingContext.Provider>
  );
};

export const useEventTheming = (): AppThemingContextType => {
  const ctx = useContext(AppThemingContext);
  if (!ctx)
    throw new Error(
      "useEventTheming must be used within an AppThemingProvider",
    );
  return ctx;
};
